/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component } from '@angular/core';
import { Router } from '@angular/router';
import { type HttpResponse, HttpService, I18nService, LoadingService, PromptService, ToastService } from '@dm/core';
import { UNPLACEHOLDER } from 'app/common/constants';

import { AuthService } from '../../../common/services';
import { PoweronFailService } from './poweron-fail.service';

const timeoutMap = {
    poweronfaillog: {
        interval: 5 * 1e3,
        package: 90 * 1e3,
        getpath: 10 * 1e3,
        check: 60 * 1e3,
    },
};

@Component({
    selector: 'app-poweron-fail',
    templateUrl: './poweron-fail.component.html',
    styleUrls: ['./poweron-fail.component.scss'],
})
export class PoweronFailComponent implements OnInit {
    // 开工失败信息
    failReason = {
        code: UNPLACEHOLDER,
        reason: UNPLACEHOLDER,
        suggestion: UNPLACEHOLDER,
    };

    // 定时查询开工失败日志路径
    getpathTimer;
    // 定时查询开工失败日志次数
    getpathCount = 0;
    // 定时查询开工失败日志最大次数
    getpathMaxTime = 720;

    // 初始化密码的状态
    initPasswordFail = false;

    i18n = {
        poweronFailTitle: this.i18nService.get('common.poweronfail_title'),
        errorCodeTitle: this.i18nService.get('common.errorcode_lab'),
        resonTitle: this.i18nService.get('monitor.task_viewDetail_failed_possCauses_lab'),
        suggestionTitle: this.i18nService.get('monitor.alarm_term_rescoverySuggestion_col'),
        downLoadLogTitle: this.i18nService.get('common.logDownLoad_lab'),
        downloadRecentIcon: 'icon-download',
        downloadAllIcon: 'icon-download',
        recentLogTitle: this.i18nService.get('system.exportData_task_para_exportRecentLog_list'),
        allLogTitle: this.i18nService.get('system.exportData_task_para_exportHistoryLog_list'),
        exportTitle: this.i18nService.get('system.exportData_task_title_label'),
        exportMsg: this.i18nService.get('system.exportData_term_exportDiagnosticFile_warn_message'),
        baseDesc: this.i18nService.get('system.exportData_term_info_Import_msg'),
        baseErr: this.i18nService.get('system.exportData_term_failExportData_label'),
        exportTimeout: this.i18nService.get('common.task_progressTimeout_tip'),
        jumpToLoginPageTitle: this.i18nService.get('common.gotoLogin_btn'),
        poweronFailDisableTip: this.i18nService.get('common.poweronfail_export_err_tip'),
    };

    constructor(
        private i18nService: I18nService,
        private poweronService: PoweronFailService,
        private authService: AuthService,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private router: Router,
        private prompt: PromptService,
        private httpService: HttpService,
    ) { }

    ngOnInit(): void {
        this.getPoweronFailReasonInfo();

        this.getURLParams();
    }

    // 下载开工失败日志
    download(type): void {
        this.prompt.warning({
            id: 'prompt-poweron-fail-download',
            title: this.i18n.exportTitle,
            content: this.i18n.exportMsg,
            onOk: () => {
                this.prompt.close();

                this.package(type);

                // 导出日志时，开启超时检测例外
                this.authService.enableTimeoutExcept();
            },
        });
    }

    // 导日志
    package(type): void {
        const param: any = {};

        if (type === 'all') {
            param.exportall = true;
        }

        const options = { timeout: timeoutMap.poweronfaillog.package };

        // 打开全局忙等
        this.loadingService.show();

        this.poweronService.doAction({ ...param, tag: 'package' }, options).subscribe(
        () => {
            this.getpathTimer = setInterval(() => {
                // 如果超过最大等待时间，停止轮询
                if (this.getpathCount > this.getpathMaxTime) {
                    // 展示超时
                    this.openError({
                        result: {
                            description: this.i18n.exportTimeout,
                            code: 0,
                        },
                        data: {},
                    });
                } else {
                    this.getPath();

                    this.getpathCount++;
                }
            }, timeoutMap.poweronfaillog.interval);
        },
        (err) => {
            this.openError(err);
        });
    }

    // 获取路径信息
    getPath(): void {
        this.poweronService.doAction({ tag: 'getpath' }).subscribe(
        (res) => {
            this.loadingService.hide();
            if (this.getpathTimer) {
                clearInterval(this.getpathTimer);
            }
            const nodeId = res.data[0].nodeid;
            // 下载日志
            this.poweronService.download({ tag: nodeId });
            // 导出日志时，关闭超时检测例外
            this.authService.disableTimeoutExcept();
        }, () => {
             // 导出日志时，关闭超时检测例外
             this.authService.disableTimeoutExcept();
        });
    }

    // 展示错误信息
    openError(response?: HttpResponse): void {
        // 关闭全局忙等
        this.loadingService.hide();

        // 错误提示信息
        let description = this.i18n.baseDesc;
        let suggestion = '';

        if (response) {
            description = response.result.description;
            suggestion = response.result.suggestion;
        }

        // 清除定时查询路径信息
        if (this.getpathTimer) {
            clearInterval(this.getpathTimer);
        }

        this.toastService.error({ description, suggestion }, this.i18n.baseErr);

        // 导出日志时，关闭超时检测例外
        this.authService.disableTimeoutExcept();
    }

    // 获取开工失败原因
    getPoweronFailReasonInfo(): void {
        this.poweronService.getPoweronFailReson({ mask: true }).subscribe(({ data }) => {
            if (data) {
                this.failReason.code = data.code || UNPLACEHOLDER;
                this.failReason.reason = data.reason || UNPLACEHOLDER;
                this.failReason.suggestion = data.suggestion || UNPLACEHOLDER;

                // 控制器离线场景(错误码：1090570497)：系统开工失败标题变更为"控制器离线。"
                if (Number(this.failReason.code) === 1090570497) {
                    this.i18n.poweronFailTitle = this.i18nService.get('common.controllerOffline_title');
                }
            }
        });
    }

    // 跳转至登录页
    jumpToLoginPage(): void {
        const rest = this.httpService.makeUrl('user.logout');
        this.httpService.delete(rest,
            {
                doException: false,
                adaptResult: (res) => {
                    // 开工失败页面清除sessions接口必定返回错误码401  特殊处理下【规避触发sessions失效展示54321提示框】
                    if (res.error.code === -401) {
                        res.error = {};
                    }
                    res.result = res.error;
                    return res;
                },
            }).subscribe(
                () => {
                    this.router.navigate(['/login']);
                },
                () => {
                    this.router.navigate(['/login']);
                });
    }

    // 获取路由参数
    getURLParams(): void {
        const url = window.location.search;
        // 只有密码未初始化成功，进入开工失败页面会携带路由参数
        if (url.indexOf('?') !== -1) {
            this.initPasswordFail = url.split('=')[1] === 'true';
        }
    }
}
